import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClipboardList,
  faUsers,
  faChartBar,
  faEnvelope,
  faComments,
  faCalendarAlt,
  faSignal,
  faBan,
  faKey,
  faCog,
  faChevronDown,
  faChevronRight,
  faChartLine,
  faAnchor,
  faCircleQuestion,
  faUserShield,
  faLock,
  faReply,
  faRightFromBracket,
  faStore,
  faRobot,
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../theme/ThemeContext';
import TokenService from '../../lib/tokenService';
import { logoutUser } from '../../lib/api';
import { getToken, createTemplate } from '../../lib/api';
import { jwtDecode } from 'jwt-decode';
import { isAdminSession } from '../../lib/adminEmail';
import AppLogo from '../../components/AppLogo';
import './Sidebar.css';

const Sidebar = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('/main/Dashboard');
  const [activeSubmenu, setActiveSubmenu] = useState('');
  const [openMenu, setOpenMenu] = useState(null);
  const sidebarRef = useRef(null);
  const [userRole, setUserRole] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('Processing...');
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const token = getToken();
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserRole(decoded?.role || 'user');
      } catch (e) {
        setUserRole('user');
      }
    } else {
      setUserRole('user');
    }
    setIsAdmin(isAdminSession());
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setSidebarOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleSidebar = useCallback(() => setSidebarOpen((v) => !v), []);
  const toggleSubmenu = useCallback((menu) => setOpenMenu((m) => (m === menu ? null : menu)), []);
  const handleMenuItemClick = useCallback((path) => {
    setActiveMenu(path);
    setActiveSubmenu('');
    if (window.innerWidth <= 768) setSidebarOpen(false);
  }, []);
  const handleSubmenuItemClick = useCallback((path) => {
    setActiveSubmenu(path);
    setActiveMenu('');
    if (window.innerWidth <= 768) setSidebarOpen(false);
  }, []);

  const handleLogout = useCallback(async () => {
    await logoutUser();
    TokenService.clearToken();
    navigate('/login', { replace: true });
    if (window.innerWidth <= 768) setSidebarOpen(false);
  }, [navigate]);

  const handleCreateTemplate = useCallback(async () => {
    const categories = ["Marketing", "Sales", "Promotion", "Feedback", "Welcome", "Reminder", "Support", "Survey"];
    const templateName = `Template ${categories[Math.floor(Math.random() * categories.length)]}`;
    const templateDescription = `This template is ideal for ${templateName.toLowerCase().replace("template ", "")} campaigns.`;

    setShowModal(true);
    setModalMessage('Processing...');
    setIsSuccess(false);

    try {
      const response = await createTemplate(templateName, templateDescription);
      const templateId = response.id;

      setTimeout(() => {
        setModalMessage('Campaign created successfully!');
        setIsSuccess(true);
        setTimeout(() => {
          setShowModal(false);
          navigate(`/main/send-template?template_id=${templateId}`);
        }, 1500);
      }, 2000);
    } catch (error) {
      console.error('Error creating new template:', error);
      setModalMessage('Failed to create a new template. Please try again later.');
      setTimeout(() => {
        setShowModal(false);
      }, 2000);
    }
  }, [navigate]);

  const Submenu = ({ menu, icon, items }) => {
    const isOpen = openMenu === menu;
    const wrapperRef = useRef(null);
    const innerRef = useRef(null);

    useEffect(() => {
      const el = wrapperRef.current;
      if (!el || !innerRef.current) return;
      const target = isOpen ? innerRef.current.scrollHeight : 0;
      el.style.maxHeight = target + 'px';
      el.style.opacity = isOpen ? '1' : '0';
    }, [isOpen, items]);

    const parentActive = useMemo(() => {
      return items.some((it) => it.path && it.path === activeSubmenu);
    }, [items, activeSubmenu]);

    return (
      <li className={`sidebar-nav-item ${isOpen ? 'open' : ''} ${parentActive ? 'active-trail' : ''}`}>
        <button className="sidebar-nav-link" onClick={() => toggleSubmenu(menu)} aria-expanded={isOpen}>
          <div className="sidebar-menu-item">
            <span className="menu-icon"><FontAwesomeIcon icon={icon} /></span>
            <span className="menu-text">{menu}</span>
            <span className={`chevron ${isOpen ? 'rotate' : ''}`}>
              <FontAwesomeIcon icon={faChevronDown} />
            </span>
          </div>
        </button>
        <div className="submenu-wrapper" ref={wrapperRef}>
          <ul ref={innerRef} className="sidebar-submenu">
            {items.map((item, index) => (
              <li key={index} className={`sidebar-submenu-item ${activeSubmenu === item.path ? 'active' : ''}`}>
                {item.render ? (
                  <div className="sidebar-submenu-link">
                    {item.render()}
                  </div>
                ) : item.onClick ? (
                  <button className={`sidebar-submenu-link ${item.label === 'Logout' ? 'danger' : ''}`} onClick={item.onClick}>
                    {item.icon && <span className="submenu-icon"><FontAwesomeIcon icon={item.icon} /></span>}
                    {item.label}
                  </button>
                ) : (
                  <Link to={item.path} className="sidebar-submenu-link" onClick={() => handleSubmenuItemClick(item.path)}>
                    {item.icon && <span className="submenu-icon"><FontAwesomeIcon icon={item.icon} /></span>}
                    {item.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      </li>
    );
  };

  const roleMenus = useMemo(() => {
    const adminSubmenu = isAdmin ? (
      <Submenu
        key="Admin"
        menu="Admin"
        icon={faUserShield}
        items={[
          { label: 'Overview', path: '/main/admin/overview' },
          { label: 'Create User', path: '/main/admin/create-user' },
          { label: 'Manage Users', path: '/main/admin/manage-users' },
          { label: 'Add Balance', path: '/main/admin/add-balance' },
          { label: 'Pricing', path: '/main/admin/pricing' },
          { label: 'Catalogue', path: '/main/admin/catalogue' },
        ]}
      />
    ) : null;

    if (userRole === 'bot') {
      return [
        adminSubmenu,
        <Submenu key="Bot" menu="Bot" icon={faAnchor} items={[
          { label: 'Builder', path: '/main/bot-builder', icon: faRobot },
          { label: 'Response', path: '/main/user_flow_responses_report' },
          { label: 'Payments', path: '/main/user_payments_report' },
        ]} />,
        <Submenu key="Settings" menu="Settings" icon={faCog} items={[
          { label: 'Catalogue', path: '/main/catalogue', icon: faStore },
          { label: 'Change Password', path: '/main/change-password', icon: faLock },
          { label: 'Auto Reply', path: '/main/auto-reply', icon: faReply },
          { label: 'Logout', onClick: handleLogout, icon: faRightFromBracket },
        ]} />,
      ].filter(Boolean);
    }
    // default user menus (mirrors old structure)
    return [
      adminSubmenu,
      <Submenu key="Templates" menu="Templates" icon={faClipboardList} items={[
        { label: 'Display', path: '/main/templates' },
        { label: 'Create', path: '/main/create-template' },
        { label: 'Edit', path: '/main/under_construction' },
        { label: 'Delete', path: '/main/site_construction' },
      ]} />,
      <Submenu key="Campaign" menu="Campaign" icon={faUsers} items={[
        { label: 'Create', onClick: handleCreateTemplate },
      ]} />,
      <Submenu key="Report" menu="Report" icon={faChartBar} items={[
        { label: 'Overview', path: '/main/overview' },
        { label: 'Summary', path: '/main/summary' },
        { label: 'Detailed Download', path: '/main/details_report' },
        { label: 'Number Search', path: '/main/number_message_report' },
        { label: 'Payments', path: '/main/user_payments_report' },
      ]} />,
      (
        <li className={`sidebar-nav-item ${activeMenu === '/main/Messages' ? 'active' : ''}`}>
          <Link to="/main/Messages" className="sidebar-nav-link" onClick={() => handleMenuItemClick('/main/Messages')}>
            <FontAwesomeIcon icon={faEnvelope} /> Messages
          </Link>
        </li>
      ),
      (
        <li className={`sidebar-nav-item ${activeMenu === '/main/chat' ? 'active' : ''}`}>
          <Link to="/main/chat" className="sidebar-nav-link" onClick={() => handleMenuItemClick('/main/chat')}>
            <FontAwesomeIcon icon={faComments} /> Live Chat
          </Link>
        </li>
      ),
      (
        <li className={`sidebar-nav-item ${activeMenu === '/main/catalogue' ? 'active' : ''}`}>
          <Link to="/main/catalogue" className="sidebar-nav-link" onClick={() => handleMenuItemClick('/main/catalogue')}>
            <FontAwesomeIcon icon={faStore} /> Catalogue
          </Link>
        </li>
      ),
      (
        <li className={`sidebar-nav-item ${activeMenu === '/main/bot-builder' ? 'active' : ''}`}>
          <Link to="/main/bot-builder" className="sidebar-nav-link" onClick={() => handleMenuItemClick('/main/bot-builder')}>
            <FontAwesomeIcon icon={faRobot} /> Bot Builder
          </Link>
        </li>
      ),
      <Submenu key="Schedule" menu="Schedule" icon={faCalendarAlt} items={[
        { label: 'Display', path: '/main/site_comming_soon' },
        { label: 'Create', path: '/main/site_construction' },
        { label: 'Delete', path: '/main/under_construction' },
      ]} />,
      <Submenu key="MIS" menu="MIS" icon={faSignal} items={[
        { label: 'WaBa Signal', path: '/main/site_under_construction' },
        { label: 'Logs', path: '/main/mis/logs' },
        { label: 'Recharge', path: '/main/site_construction' },
        { label: 'DayCount', path: '/main/site_comming_soon' },
      ]} />,
      <Submenu key="Black List" menu="Black List" icon={faBan} items={[
        { label: 'Display', path: '/main/site_comming_soon' },
        { label: 'Add', path: '/main/site_construction' },
        { label: 'Remove', path: '/main/under_construction' },
      ]} />,
      <Submenu key="API" menu="API" icon={faKey} items={[
        { label: 'View', path: '/main/site_under_construction' },
        { label: 'Request', path: '/main/swagger' },
        { label: 'Remove', path: '/main/site_comming_soon' },
      ]} />,
      (
        <Submenu
          key="Settings"
          menu="Settings"
          icon={faCog}
          items={[
            {
              render: () => (
                <div className="submenu-toggle-row">
                  <span>Dark Mode</span>
                  <label className="theme-switch">
                    <input type="checkbox" checked={theme === 'dark'} onChange={toggleTheme} />
                    <span className="slider" />
                  </label>
                </div>
              ),
            },
            { label: 'Help', path: '#help', icon: faCircleQuestion },
            { label: 'Catalogue', path: '/main/catalogue', icon: faStore },
            { label: 'Bot Builder', path: '/main/bot-builder', icon: faRobot },
            { label: 'Change Password', path: '/main/change-password', icon: faLock },
            { label: 'Auto Reply', path: '/main/auto-reply', icon: faReply },
            { label: 'Logout', onClick: handleLogout, icon: faRightFromBracket },
          ]}
        />
      ),
    ].filter(Boolean);
  }, [userRole, isAdmin, openMenu, activeSubmenu, handleSubmenuItemClick, handleMenuItemClick, handleCreateTemplate, handleLogout, theme, toggleTheme]);

  const Modal = ({ message, showModal, onClose, isSuccess }) => {
    if (!showModal) return null;

    return (
      <div className="modal-backdroptl" style={{ zIndex: 10000 }}>
        <div className="modal-contenttl">
          <div className="modal-bodytl">
            {!isSuccess ? (
              <div className="loading-spinnertl"></div>
            ) : (
              <div className="checkmark-containertl">
                <div className="checkmarktl"></div>
              </div>
            )}
            <h2>{message}</h2>
          </div>
          {isSuccess && (
            <button onClick={onClose} className="close-btntl">
              Close
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="hamre">
        <button className={`hamburger-button ${sidebarOpen ? 'open' : ''}`} onClick={toggleSidebar}>☰</button>
      </div>
      <div className={`sidebar ${theme} ${sidebarOpen ? 'open' : ''}`} style={{ zIndex: 999 }} ref={sidebarRef}>
        <ul className="sidebar-nav">
          <li className="sidebar-brand-wrap">
            <AppLogo to="/main/Dashboard" variant="sidebar" onClick={() => handleMenuItemClick('/main/Dashboard')} />
          </li>
          <li className={`sidebar-nav-item ${activeMenu === '/main/Dashboard' ? 'active' : ''}`}>
            <Link to="/main/Dashboard" className="sidebar-nav-link" onClick={() => handleMenuItemClick('/main/Dashboard')}>
              <FontAwesomeIcon icon={faChartLine} /> Dashboard
            </Link>
          </li>
          {roleMenus}
        </ul>
      </div>
      <Modal
        message={modalMessage}
        showModal={showModal}
        onClose={() => setShowModal(false)}
        isSuccess={isSuccess}
      />
    </>
  );
};

export default Sidebar;
